import React, { useState, useEffect } from 'react';
import { COVER_BG_IMAGE } from '../constants/cover';
import { matchesAPI, contentAPI } from '../services/api';
import { getLogoUrl } from '../utils/logoUrl';
import { FaUserTie, FaFlag, FaCalendar, FaFutbol, FaMapMarkerAlt, FaClock } from 'react-icons/fa';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { useLanguage } from '../context/LanguageContext';

const DesignationsPage = () => {
  const { language } = useLanguage();
  const [designations, setDesignations] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming'); // upcoming, all

  useEffect(() => {
    loadDesignations();
  }, []);

  const loadDesignations = async () => {
    try {
      setLoading(true);
      const [designationsRes, matchesRes] = await Promise.all([
        contentAPI.getDesignations(),
        matchesAPI.getAll()
      ]);
      
      // Combiner les désignations avec les informations des matchs
      const designationsWithMatches = (designationsRes.data || []).map(designation => {
        const match = (matchesRes.data || []).find(m => m.id === designation.match_id);
        return {
          ...designation,
          match: match || {}
        };
      });
      
      setDesignations(designationsWithMatches);
      setMatches(matchesRes.data || []);
    } catch (error) {
      console.error('Erreur:', error);
      setDesignations([]);
      setMatches([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Filtrer les désignations selon le filtre sélectionné
  const filteredDesignations = filter === 'upcoming' 
    ? designations.filter(d => d.match && d.match.status === 'upcoming')
    : designations;

  // Grouper par journée
  const designationsByRound = filteredDesignations.reduce((acc, designation) => {
    const round = designation.match?.round || 'Sans journée';
    if (!acc[round]) acc[round] = [];
    acc[round].push(designation);
    return acc;
  }, {});

  const text = {
    ar: {
      title: 'التعيينات',
      subtitle: 'تعيينات الحكام للمباريات',
      upcoming: 'المباريات القادمة',
      all: 'جميع المباريات',
      round: 'الجولة',
      referee: 'الحكم الرئيسي',
      assistant1: 'المساعد الأول',
      assistant2: 'المساعد الثاني',
      fourthOfficial: 'الحكم الرابع',
      observer: 'المراقب',
      matchDetails: 'تفاصيل المباراة',
      date: 'التاريخ',
      time: 'الوقت',
      venue: 'الملعب',
      noMatches: 'لا توجد مباريات',
      totalDesignations: 'تعيين'
    },
    fr: {
      title: 'Désignations',
      subtitle: 'Désignations des arbitres pour les matchs',
      upcoming: 'Matchs à venir',
      all: 'Tous les matchs',
      round: 'Journée',
      referee: 'Arbitre principal',
      assistant1: 'Assistant 1',
      assistant2: 'Assistant 2',
      fourthOfficial: 'Quatrième arbitre',
      observer: 'Observateur',
      matchDetails: 'Détails du match',
      date: 'Date',
      time: 'Heure',
      venue: 'Stade',
      noMatches: 'Aucun match',
      totalDesignations: 'Désignations'
    }
  };

  const t = text[language];

  return (
    <div style={styles.container}>
      {/* Header */}
      <section style={styles.header}>
        <h1 style={styles.title}>
          {language === 'ar' ? 'التعيينات' : 'Désignations'}
        </h1>
        <p style={styles.subtitle}>
          {language === 'ar' ? 'تعيينات الحكام للمباريات' : 'Désignations des arbitres pour les matchs'}
        </p>
        <div style={styles.statsBar}>
          <div style={styles.stat}>
            <span style={styles.statNumber}>{filteredDesignations.length}</span>
            <span style={styles.statLabel}>
              {language === 'ar' ? 'تعيين' : 'Désignations'}
            </span>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section style={styles.filterSection}>
        <div style={styles.filterTabs}>
          <button
            style={{...styles.filterTab, ...(filter === 'upcoming' && styles.filterTabActive)}}
            onClick={() => setFilter('upcoming')}
          >
            <FaCalendar /> {language === 'ar' ? 'المباريات القادمة' : 'Matchs à venir'}
          </button>
          <button
            style={{...styles.filterTab, ...(filter === 'all' && styles.filterTabActive)}}
            onClick={() => setFilter('all')}
          >
            <FaFutbol /> {language === 'ar' ? 'جميع المباريات' : 'Tous les matchs'}
          </button>
        </div>
      </section>

      {/* Designations by Round */}
      <section style={styles.designationsSection}>
        {Object.keys(designationsByRound).length > 0 ? (
          Object.entries(designationsByRound)
            .sort(([a], [b]) => b - a)
            .map(([round, roundDesignations]) => (
            <div key={round} style={styles.roundSection}>
              <h2 style={styles.roundTitle}>
                <FaFlag style={styles.roundIcon} />
                {language === 'ar' ? `الجولة ${round}` : `Journée ${round}`}
              </h2>
              
              <div style={styles.matchesGrid}>
                {roundDesignations.map((designation, index) => {
                  const match = designation.match || {};
                  return (
                    <div 
                      key={designation.id}
                      style={{
                        ...styles.designationCard,
                        animationDelay: `${index * 0.1}s`
                      }}
                    >
                      {/* Match Info */}
                      <div style={styles.matchInfo}>
                        <div style={styles.matchHeader}>
                          <div style={styles.matchTeams}>
                            <div style={styles.team}>
                              {getLogoUrl(match.home_team_logo) && (
                                <img 
                                  src={getLogoUrl(match.home_team_logo)} 
                                  alt="" 
                                  style={styles.teamLogo}
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              )}
                              <span style={styles.teamName}>{match.home_team_name || 'Équipe 1'}</span>
                            </div>
                            <div style={styles.vs}>VS</div>
                            <div style={styles.team}>
                              {getLogoUrl(match.away_team_logo) && (
                                <img 
                                  src={getLogoUrl(match.away_team_logo)} 
                                  alt="" 
                                  style={styles.teamLogo}
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              )}
                              <span style={styles.teamName}>{match.away_team_name || 'Équipe 2'}</span>
                            </div>
                          </div>
                        </div>

                        {match.match_date && (
                          <div style={styles.matchMeta}>
                            <div style={styles.metaItem}>
                              <FaCalendar style={styles.metaIcon} />
                              <span>
                                {new Date(match.match_date).toLocaleDateString(
                                  language === 'ar' ? 'ar-TN' : 'fr-FR',
                                  { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
                                )}
                              </span>
                            </div>
                            <div style={styles.metaItem}>
                              <FaClock style={styles.metaIcon} />
                              <span>
                                {new Date(match.match_date).toLocaleTimeString(
                                  language === 'ar' ? 'ar-TN' : 'fr-FR',
                                  { hour: '2-digit', minute: '2-digit' }
                                )}
                              </span>
                            </div>
                            {match.venue && (
                              <div style={styles.metaItem}>
                                <FaMapMarkerAlt style={styles.metaIcon} />
                                <span>{match.venue}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Referees Section */}
                      <div style={styles.refereesSection}>
                        <h4 style={styles.refereesTitle}>
                          <FaUserTie style={styles.refereesIcon} />
                          {language === 'ar' ? 'طاقم التحكيم' : 'Corps arbitral'}
                        </h4>
                        
                        <div style={styles.refereesList}>
                          {/* Main Referee */}
                          <div style={styles.refereeItem}>
                            <div style={styles.refereeRole}>
                              <div style={{...styles.roleIcon, background: '#c41e3a'}}>👨‍⚖️</div>
                              <span style={styles.roleLabel}>
                                {language === 'ar' ? 'الحكم الرئيسي' : 'Arbitre principal'}
                              </span>
                            </div>
                            <div style={styles.refereeName}>
                              {language === 'ar' ? designation.main_referee_ar || designation.main_referee : designation.main_referee}
                            </div>
                          </div>

                          {/* Assistants */}
                          <div style={styles.assistantsGrid}>
                            <div style={styles.refereeItem}>
                              <div style={styles.refereeRole}>
                                <div style={{...styles.roleIcon, background: '#2196F3'}}>🚩</div>
                                <span style={styles.roleLabel}>
                                  {language === 'ar' ? 'المساعد الأول' : 'Assistant 1'}
                                </span>
                              </div>
                              <div style={styles.refereeName}>{designation.assistant1}</div>
                            </div>

                            <div style={styles.refereeItem}>
                              <div style={styles.refereeRole}>
                                <div style={{...styles.roleIcon, background: '#2196F3'}}>🚩</div>
                                <span style={styles.roleLabel}>
                                  {language === 'ar' ? 'المساعد الثاني' : 'Assistant 2'}
                                </span>
                              </div>
                              <div style={styles.refereeName}>{designation.assistant2}</div>
                            </div>
                          </div>

                          {/* Fourth Official */}
                          {designation.fourth_official && (
                            <div style={styles.refereeItem}>
                              <div style={styles.refereeRole}>
                                <div style={{...styles.roleIcon, background: '#FF9800'}}>4️⃣</div>
                                <span style={styles.roleLabel}>
                                  {language === 'ar' ? 'الحكم الرابع' : 'Quatrième arbitre'}
                                </span>
                              </div>
                              <div style={styles.refereeName}>{designation.fourth_official}</div>
                            </div>
                          )}

                          {/* Observer */}
                          {designation.observer && (
                            <div style={styles.refereeItem}>
                              <div style={styles.refereeRole}>
                                <div style={{...styles.roleIcon, background: '#4CAF50'}}>👁️</div>
                                <span style={styles.roleLabel}>
                                  {language === 'ar' ? 'المراقب' : 'Observateur'}
                                </span>
                              </div>
                              <div style={styles.refereeName}>{designation.observer}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div style={styles.noData}>
            <FaCalendar style={styles.noDataIcon} />
            <p>{language === 'ar' ? 'لا توجد تعيينات' : 'Aucune désignation'}</p>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5'
  },
  header: {
    backgroundImage: `linear-gradient(135deg, rgba(26, 54, 93, 0.52) 0%, rgba(44, 82, 130, 0.48) 100%), url(${COVER_BG_IMAGE})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: '80px 20px 60px',
    textAlign: 'center',
    color: 'white'
  },
  title: {
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '15px',
    textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
  },
  subtitle: {
    fontSize: '20px',
    marginBottom: '30px',
    opacity: 0.9
  },
  statsBar: {
    display: 'flex',
    justifyContent: 'center',
    gap: '40px',
    marginTop: '30px'
  },
  stat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    background: 'rgba(255,255,255,0.2)',
    padding: '20px 40px',
    borderRadius: '15px',
    backdropFilter: 'blur(10px)'
  },
  statNumber: {
    fontSize: '36px',
    fontWeight: 'bold'
  },
  statLabel: {
    fontSize: '16px',
    marginTop: '5px'
  },
  filterSection: {
    background: 'white',
    borderBottom: '2px solid #e0e0e0',
    position: 'sticky',
    top: 0,
    zIndex: 100
  },
  filterTabs: {
    display: 'flex',
    maxWidth: '1200px',
    margin: '0 auto',
    gap: '10px',
    padding: '0 20px'
  },
  filterTab: {
    flex: 1,
    padding: '15px 20px',
    background: 'transparent',
    border: 'none',
    borderBottom: '3px solid transparent',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'all 0.3s'
  },
  filterTabActive: {
    color: '#c41e3a',
    borderBottomColor: '#c41e3a'
  },
  designationsSection: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '40px 20px'
  },
  roundSection: {
    marginBottom: '50px'
  },
  roundTitle: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '30px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    paddingBottom: '15px',
    borderBottom: '3px solid #c41e3a'
  },
  roundIcon: {
    color: '#c41e3a',
    fontSize: '24px'
  },
  matchesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(450px, 1fr))',
    gap: '30px'
  },
  designationCard: {
    background: 'white',
    borderRadius: '15px',
    overflow: 'hidden',
    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
    transition: 'all 0.3s',
    animation: 'fadeInUp 0.6s ease forwards',
    opacity: 0
  },
  matchInfo: {
    padding: '25px',
    background: 'linear-gradient(135deg, #f9f9f9 0%, #f0f0f0 100%)',
    borderBottom: '2px solid #e0e0e0'
  },
  matchHeader: {
    marginBottom: '20px'
  },
  matchTeams: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '15px'
  },
  team: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px'
  },
  teamLogo: {
    width: '50px',
    height: '50px',
    objectFit: 'contain'
  },
  teamName: {
    fontSize: '14px',
    fontWeight: '600',
    textAlign: 'center',
    color: '#333'
  },
  vs: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#999',
    padding: '0 10px'
  },
  matchMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#666'
  },
  metaIcon: {
    color: '#c41e3a',
    fontSize: '14px'
  },
  refereesSection: {
    padding: '25px'
  },
  refereesTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    paddingBottom: '12px',
    borderBottom: '2px solid #f0f0f0'
  },
  refereesIcon: {
    color: '#c41e3a',
    fontSize: '18px'
  },
  refereesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  assistantsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px'
  },
  refereeItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px',
    background: '#f9f9f9',
    borderRadius: '10px',
    transition: 'all 0.3s'
  },
  refereeRole: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  roleIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px'
  },
  roleLabel: {
    fontSize: '13px',
    color: '#666',
    fontWeight: '600'
  },
  refereeName: {
    fontSize: '15px',
    fontWeight: 'bold',
    color: '#333',
    paddingRight: '40px'
  },
  noData: {
    textAlign: 'center',
    padding: '80px 20px',
    color: '#999'
  },
  noDataIcon: {
    fontSize: '64px',
    marginBottom: '20px',
    opacity: 0.3
  }
};

// Ajouter l'animation CSS
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    .matchesGrid {
      grid-template-columns: 1fr !important;
    }
    .assistantsGrid {
      grid-template-columns: 1fr !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default DesignationsPage;
